/**
 * @fileoverview Type definitions for the optimization queue system
 * 
 * Centralized types shared across all optimization queue hooks.
 */

import type { OptimizationJob, OptimizationEvent } from '../../services/optimization/OptimizationEventService';
import type { ApiTypes } from '../../services/api/endpoints';

// Re-export for convenience
export type { OptimizationJob, OptimizationEvent };
export type { ApiTypes };

/**
 * Options for configuring the optimization queue hooks
 */
export interface UseOptimizationQueueOptions {
  /** Whether to automatically connect to SSE on mount */
  autoConnect?: boolean;
  /** Polling interval in milliseconds (default: 3000) */
  pollInterval?: number;
  /** Maximum retry attempts for failed connections */
  maxRetries?: number;
}

/**
 * Complete state of the optimization queue
 */
export interface OptimizationQueueState {
  /** All tracked jobs */
  jobs: OptimizationJob[];
  /** Jobs currently running */
  activeJobs: OptimizationJob[];
  /** Jobs waiting to run */
  queuedJobs: OptimizationJob[];
  /** Successfully completed jobs */
  completedJobs: OptimizationJob[];
  /** Jobs that encountered errors */
  failedJobs: OptimizationJob[];
  /** Whether jobs are being fetched */
  loading: boolean;
  /** Error message if any operation failed */
  error: string | null;
  /** Whether SSE connection is active */
  connected: boolean;
  /** Whether polling should be active */
  shouldPoll: boolean;
  /** Currently selected job ID for viewing results */
  selectedJobId: string | null;
}

/**
 * Initial state for the optimization queue
 */
export const INITIAL_QUEUE_STATE: OptimizationQueueState = {
  jobs: [],
  activeJobs: [],
  queuedJobs: [],
  completedJobs: [],
  failedJobs: [],
  loading: false,
  error: null,
  connected: false,
  shouldPoll: false,
  selectedJobId: null,
};

/**
 * Actions available for job management
 */
export interface JobActions {
  /** Start a new optimization */
  startOptimization: (request: ApiTypes.OptimizationRequest) => Promise<string>;
  /** Cancel a running optimization */
  cancelOptimization: (jobId: string) => Promise<void>;
  /** Remove a job from the list */
  removeJob: (jobId: string) => Promise<void>;
}

/**
 * SSE connection controls
 */
export interface SSEConnectionControls {
  /** Connect to SSE server */
  connect: () => void;
  /** Disconnect from SSE server */
  disconnect: () => void;
  /** Whether currently connected */
  connected: boolean;
}

/**
 * Polling controls
 */
export interface PollingControls {
  /** Start polling for job updates */
  startPolling: () => void;
  /** Stop polling for job updates */
  stopPolling: () => void;
  /** Whether polling is currently active */
  shouldPoll: boolean;
}

/**
 * Job selection controls
 */
export interface JobSelectionControls {
  /** Currently selected job ID */
  selectedJobId: string | null;
  /** Select a job by ID */
  selectJob: (jobId: string | null) => void;
}

/**
 * Result of job state updates
 */
export interface JobStateUpdate {
  jobs: OptimizationJob[];
  activeJobs: OptimizationJob[];
  queuedJobs: OptimizationJob[];
  completedJobs: OptimizationJob[];
  failedJobs: OptimizationJob[];
  selectedJobId: string | null;
  shouldPoll: boolean;
}
